import { useQuery } from '@apollo/client';
import { useRouter } from '@application/utils';
import { graphql } from '@gql/gql';
import type {
  LatLngPoint,
  ProGetRelatedProfessionalsQuery,
  ProGetRelatedProfessionalsQueryVariables,
  ProfessionalTypeEnum,
} from '@gql/graphql';
import { useMemo } from 'react';

export const PRO_GET_RELATED_PROFESSIONALS = graphql(`
  query ProGetRelatedProfessionals(
    $nearByPoint: LatLngPoint
    $take: Int
    $proId: Int
    $country: CountryCode!
    $language: String!
    $proType: ProfessionalTypeEnum
  ) {
    relatedProfessionals(
      nearByPoint: $nearByPoint
      country: $country
      language: $language
      proId: $proId
      take: $take
      proType: $proType
    ) {
      locations {
        city
        region
      }
      pros {
        id
        type
        translations {
          id
          url
          title
          language
        }
      }
    }
  }
`);

export interface RelatedProfessionalsParams {
  proType: ProfessionalTypeEnum;
  proId?: number;
  nearByPoint?: LatLngPoint;
}

export const useGetRelatedProfessionals = (params: RelatedProfessionalsParams) => {
  const { proType, nearByPoint, proId } = params;
  const { countryCode, language } = useRouter();
  const variables = useMemo((): ProGetRelatedProfessionalsQueryVariables => {
    return {
      take: 10,
      proType,
      nearByPoint,
      country: countryCode,
      language,
      proId,
    };
  }, [countryCode, language, proId, nearByPoint, proType]);

  const { data, error, loading } = useQuery<
    ProGetRelatedProfessionalsQuery,
    ProGetRelatedProfessionalsQueryVariables
  >(PRO_GET_RELATED_PROFESSIONALS, {
    variables,
    skip: nearByPoint == null,
    ssr: false,
  });

  return {
    locations: data?.relatedProfessionals.locations ?? [],
    otherPros: data?.relatedProfessionals.pros ?? [],
    relatedProsError: error,
    relatedProsLoading: loading,
  };
};
